import { acceptHMRUpdate, defineStore } from "pinia";
import { useNgSession } from "@/shared/ng/ng-session";
import { computed, Ref, ref, watch, watchEffect } from "vue";
import { Token, WsToken } from "@/shared/model/auth";
import useAuthService from "@/shared/composables/use-auth-service";
import { getCashoutSubscription } from "@/api/sports-service-api";
import { isDevelopmentEnv } from "@/shared/utils/environment-variables";
import useConfirmModal from "@/shared/composables/use-confirm-modal";
import { isNgUIVisible, ngFrameworkLoaded } from "@/shared/ng/ng-integration";
import { logCustomException } from "@/shared/utils/logger/use-app-logger";
import { useCookieStorage } from "@/shared/composables/use-storage";
import pRetry from "p-retry";
import { waitUntilTrue } from "@/shared/utils/wait";

const parseWsTokenString = (str: string | undefined): WsToken => {
    const tokenString = str?.split(":");
    return tokenString?.length === 3
        ? {
              expiration_date: Number(tokenString[0]),
              channel: tokenString[1],
              token: tokenString[2],
          }
        : undefined;
};

export const useAuthStore = defineStore("Auth", () => {
    const storage = useCookieStorage();
    const authService = useAuthService();
    const { isLoggedIn, session } = useNgSession();

    // state
    const token: Ref<Token> = ref();
    const refreshTokenAtTimestamp = ref(0);
    let refreshIntervalId: number = null;

    const wsToken: Ref<WsToken> = ref();
    const refreshWsTokenAtTimestamp = ref(0);
    let refreshWsIntervalId = null;

    // getters
    const isTokenAvailable = computed(() => !!token.value?.access_token);
    const isWsTokenAvailable = computed(() => !!wsToken.value?.token);

    // Dejv : nebylo by lepší použití watche ?
    // Ondra: Tahle funkce by se primarne mela vyuzivat v asynchronnich volanich na zabezpecene api kde ma zabranit tomu, aby doslo k provolani bez tokenu (napr. v pripade kde auth store jeste neni plne inicializovan ci pri vypadku kdy bude jeste dochazet opetovnemu ziskani tokenu).
    const waitUntilTokenAvailable = async (time = 100, timeout: number = 120_000) => {
        await waitUntilTrue(
            () => isTokenAvailable.value && refreshTokenAtTimestamp.value > Date.now(),
            time,
            timeout,
        ).catch(() => {
            logCustomException("auth-store", `AUTH Token retrieval timed out. Timeout interval ${timeout} breached.`);
        });
    };

    // additional logic
    const initFromCookies = () => {
        token.value = storage.getJson("auth_access_token") || undefined;
        wsToken.value = storage.getJson("auth_access_ws_token") || undefined;
    };

    const setupWatchers = () => {
        watchEffect(() => setCookie("auth_access_token", token.value, { sameSite: "strict" }));
        watchEffect(() => setCookie("auth_access_ws_token", wsToken.value, { sameSite: "strict" }));
    };

    const init = () => {
        initFromCookies();
        setupWatchers();
        ngFrameworkLoaded().finally(() => {
            watch(isLoggedIn, watchLoggedIn, { immediate: true });
        });
    };

    init();

    // trigger login/logout based on NG session state
    async function watchLoggedIn(loggedIn: boolean) {
        if (!loggedIn) {
            if (isTokenAvailable.value) {
                await logoutFromOb();
            }
            return;
        }

        if (!isTokenAvailable.value) {
            token.value = await fetchObSessionToken();
        }

        // give vue some time to propagate reactivity
        setTimeout(async () => {
            if (!isWsTokenAvailable.value) {
                wsToken.value = await fetchObWebSocketToken();
            }
        }, 0);
    }

    // calculate new refresh timestamp when OB token changes
    watch(
        token,
        (newToken, oldToken) => {
            if (newToken?.access_token === oldToken?.access_token && newToken?.issued_at === oldToken?.issued_at)
                return;
            if (!newToken) {
                refreshTokenAtTimestamp.value = 0;
                return;
            }

            // expires_in is in seconds, we need to convert it to milliseconds
            // we'll refresh token 60s before it expires
            refreshTokenAtTimestamp.value = newToken.issued_at + newToken.expires_in * 1_000 - 60_000;
            if (isDevelopmentEnv())
                console.log("AUTH Token should be refreshed at: ", new Date(refreshTokenAtTimestamp.value));
        },
        { immediate: true },
    );

    // calculate new refresh timestamp when WS token changes
    watch(
        wsToken,
        (newToken, oldToken) => {
            if (newToken?.token === oldToken?.token && newToken?.expiration_date === oldToken?.expiration_date) return;

            if (!newToken) {
                refreshWsTokenAtTimestamp.value = 0;
                return;
            }

            // expiration_date is in seconds, we need to convert it to milliseconds
            // we'll refresh token 30s before it expires
            refreshWsTokenAtTimestamp.value = newToken.expiration_date * 1_000 - 30_000;
            if (isDevelopmentEnv())
                console.log("AUTH WS Token should be refreshed at: ", new Date(refreshWsTokenAtTimestamp.value));
        },
        { immediate: true },
    );

    // WS token depends on OB token... without OB token it we cannot get WS token
    watch(
        isTokenAvailable,
        async () => {
            if (!isTokenAvailable.value) return;
            if (isWsTokenAvailable.value) return;

            wsToken.value = await fetchObWebSocketToken();
        },
        { immediate: true },
    );

    // trigger refresh before token expires based on expires_in value
    watch(
        refreshTokenAtTimestamp,
        async (timestamp) => {
            // 1. clears interval (even if newTimestamp is not set)
            // 2. if newTimestamp is set, set new timeout to schedule refresh
            clearInterval(refreshIntervalId);
            refreshIntervalId = null;

            if (!timestamp) return;

            if (timestamp < Date.now()) {
                token.value = await fetchObSessionToken();
            } else {
                refreshIntervalId = setInterval(async () => {
                    if (timestamp < Date.now()) {
                        clearInterval(refreshIntervalId);
                        token.value = await fetchObSessionToken();
                    }
                }, 1_000);
            }
        },
        { immediate: true },
    );

    // trigger refresh before token expires based on expires_in value
    watch(
        refreshWsTokenAtTimestamp,
        async (timestamp) => {
            // 1. clears interval (even if newTimestamp is not set)
            // 2. if newTimestamp is set, set new timeout to schedule refresh
            clearInterval(refreshWsIntervalId);
            refreshWsIntervalId = null;

            if (!timestamp) return;

            if (timestamp < Date.now()) {
                wsToken.value = await fetchObWebSocketToken();
            } else {
                refreshWsIntervalId = setInterval(async () => {
                    if (timestamp < Date.now()) {
                        clearInterval(refreshWsIntervalId);
                        wsToken.value = await fetchObWebSocketToken();
                    }
                }, 1_000);
            }
        },
        { immediate: true },
    );

    async function fetchObWebSocketToken() {
        try {
            if (!isTokenAvailable.value) {
                const message = "OB token data is missing";
                logCustomException("auth-store", message);
                if (isDevelopmentEnv()) console.log(message, token.value);
                return;
            }
            // There could be some edge case where token is not available so we want to have it in logs hence waitUntilTokenAvailable() is here and not higher.
            // When token is restored from cookie it might be expired already, and it needs to be refreshed.
            // Here we wait until we have valid (not expired) token available. Refresh mechanism should be running at this moment, we just need to wait until it's done.
            await waitUntilTokenAvailable();
            const response = await pRetry(getCashoutSubscription, { retries: 2 });
            if (!response) {
                logCustomException("auth-store", "AUTH WS Token refresh failed", { response: response });
            }
            return parseWsTokenString(response?.data?.streams?.[0]?.value?.token);
        } catch (error) {
            logCustomException("auth-store", "AUTH WS Token refresh failed with error", { error: error });
        }
    }

    async function fetchObSessionToken() {
        await ngFrameworkLoaded();
        if (!session.value?.SessionToken || !session.value?.PlayerID) {
            const message = "Session is missing. Attempting to reinitialize...";
            if (isDevelopmentEnv()) console.error("Session is missing. Attempting to reinitialize...");
            logCustomException("auth-store", message);

            useNgSession().refreshSession(); // Reinitialize the session when session not found?
            if (!session.value?.SessionToken || !session.value?.PlayerID) {
                const message = "NG session data is missing after reinitialization";
                logCustomException("auth-store", message);
                if (isDevelopmentEnv()) console.log(message, session.value);
                return;
            }
        }
        const newToken = await authService.login(session.value?.SessionToken, session.value?.PlayerID);
        if (!newToken) {
            logCustomException("auth-store", "OB login failed");
            ngLogoutUnauthorized();
        }
        return newToken;
    }

    async function logoutFromOb() {
        const newToken = await authService.logout();
        // If there will be some response (other than undefined) it means that something went wrong
        if (newToken) {
            logCustomException("auth-store", "OB logout failed", { obResponse: newToken });
        }

        token.value = null;
        wsToken.value = null;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function setCookie(name: string, value: any, options?: Cookies.CookieAttributes) {
        if (value) {
            storage.setJson(name, value, "essential", options);
        } else {
            storage.removeItem(name);
        }
    }

    return {
        isLoggedIn,
        token,
        refreshTokenAtTimestamp,
        isTokenAvailable,
        wsToken,
        refreshWsTokenAtTimestamp,
        isWsTokenAvailable,
        waitUntilTokenAvailable,
    };
});

function openAuthErrorModal() {
    const { isLoggedIn } = useNgSession();
    if (!isLoggedIn.value) return;
    // give NG some time to show logout popup if necessary
    setTimeout(() => {
        // if NG UI is visible, we don't want to show our modal
        if (isNgUIVisible.value) return;

        const { openModal } = useConfirmModal();
        openModal({
            title: "Přihlášení se nezdařilo",
            text: "Nastal problém s přihlášením k serveru kurzových sázek. Zkuste to prosím znovu.",
            cancelText: "Zkusit znovu",
        });
    }, 500);
}

export const refreshTokens = async () => {
    logCustomException("token-refresh", "had to refresh token manually");
    await ngFrameworkLoaded();
    if (!useNgSession().isLoggedIn.value) return;

    const authStore = useAuthStore();
    authStore.token = null;
    authStore.refreshTokenAtTimestamp = 0;
    authStore.wsToken = null;
    authStore.refreshWsTokenAtTimestamp = 0;

    await authStore.waitUntilTokenAvailable(100, 10_000);
    if (!authStore.isTokenAvailable) {
        logCustomException("token-refresh", "Refresh token failed");
        throw new Error("Token refresh failed"); // Throw an error to trigger the catch block
    }
};

export const ngLogoutUnauthorized = async () => {
    await ngFrameworkLoaded();
    if (!useNgSession().isLoggedIn.value) return;

    logCustomException("auth-store", "Logging out on unauthorized response");

    //ngLogoutUnauthorized is called from multiple places tho logout happens asynchronously
    //and we need to make sure that the session is cleared after first logout call
    //then the above if statement will prevent the second (or third or twelfth) logout call from clearing the session again
    //BEWARE: this is a very unstable workaround with possible side effects - the probability of this happening is very low tho. But it is still possible. So we should fix this in the future. Right? Or we can just leave it like this and hope for the best for the next 10 years.

    useNgSession().clearSession();

    openAuthErrorModal();
    ng_fw.logout();
};

// Enable HMR
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot));
}
